"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { apiUrl, authHeaders } from "@/lib/api"
import FetchError from "./fetch-error"

interface ProdutoDiario {
  dia: string
  kabum: number
  terabyte: number
}

interface DescontoLoja {
  loja: string
  avgDesconto: number
  totalProdutos: number
}

interface ProductForChart {
  store?: { name?: string } | null
  created_at?: string
  discount_percentage?: number
}

function buildDailyData(products: ProductForChart[]): ProdutoDiario[] {
  const dias: Record<string, { kabum: number; terabyte: number }> = {}
  const hoje = new Date()
  for (let i = 13; i >= 0; i--) {
    const d = new Date(hoje)
    d.setDate(d.getDate() - i)
    const key = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
    dias[key] = { kabum: 0, terabyte: 0 }
  }
  for (const p of products) {
    if (!p.created_at) continue
    const d = new Date(p.created_at)
    const key = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
    if (!dias[key]) continue
    const nome = (p.store?.name || "").toLowerCase()
    if (nome.includes("kabum")) dias[key].kabum++
    else if (nome.includes("terabyte")) dias[key].terabyte++
  }
  return Object.entries(dias).map(([dia, v]) => ({ dia, ...v }))
}

interface TooltipPayloadItem {
  dataKey?: string | number
  name?: string
  value?: number | string
  color?: string
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipPayloadItem[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null
  return (
    <div className="rounded-lg border border-border/60 bg-popover/95 px-3 py-2 text-xs shadow-lg backdrop-blur-md">
      <div className="mb-1.5 font-medium text-foreground tabular-nums">{label}</div>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={String(p.dataKey)} className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
            <span className="text-muted-foreground">{p.name}</span>
            <span className="ml-auto pl-3 font-semibold text-foreground tabular-nums">{p.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ContentChart() {
  const [dadosDiarios, setDadosDiarios] = useState<ProdutoDiario[]>([])
  const [dadosLojas, setDadosLojas] = useState<DescontoLoja[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
      const headers = authHeaders(token)

      const [prodRes, storeRes] = await Promise.all([
        fetch(`${apiUrl()}/products/?page_size=300&ordering=-created_at`, { headers }).then((r) => r.json()),
        fetch(`${apiUrl()}/stores/`, { headers }).then((r) => r.json()),
      ])

      // Sessão expirada devolve {"detail": ...}, que é truthy: sem o
      // Array.isArray o objeto de erro virava a lista e estourava o for..of.
      const produtos = (prodRes?.results || (Array.isArray(prodRes) ? prodRes : [])) as ProductForChart[]
      setDadosDiarios(buildDailyData(produtos))

      const stores = (storeRes?.results || (Array.isArray(storeRes) ? storeRes : [])) as { name: string }[]
      const lojas: DescontoLoja[] = stores.map((s) => {
        const prods = produtos.filter((p) => {
          const nome = (p.store?.name || "").toLowerCase()
          return nome.includes(s.name.toLowerCase().slice(0, 5))
        })
        const avg = prods.length
          ? Math.round(prods.reduce((acc, p) => acc + parseFloat(String(p.discount_percentage || 0)), 0) / prods.length)
          : 0
        return { loja: s.name, avgDesconto: avg, totalProdutos: prods.length }
      })
      setDadosLojas(lojas)
    } catch (e) {
      console.error("ContentChart fetch error:", e)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const summary = useMemo(() => {
    const totalKabum = dadosDiarios.reduce((acc, d) => acc + d.kabum, 0)
    const totalTera = dadosDiarios.reduce((acc, d) => acc + d.terabyte, 0)
    let peakDay = ""
    let peakValue = 0
    for (const d of dadosDiarios) {
      const sum = d.kabum + d.terabyte
      if (sum > peakValue) { peakValue = sum; peakDay = d.dia }
    }
    return { totalKabum, totalTera, peakDay, peakValue }
  }, [dadosDiarios])

  const maxLojaPct = Math.max(...dadosLojas.map((l) => l.avgDesconto), 1)

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.6fr_1fr] lg:gap-6">
      {/* Coleta diária */}
      <div className="surface-card min-w-0">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Coleta diária</div>
            <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">
              Produtos capturados
            </h3>
          </div>
          <span className="text-xs text-muted-foreground">últimos 14 dias</span>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: "var(--brand-amber)" }} />
            <span className="text-muted-foreground">KaBuM!</span>
            <span className="font-mono font-semibold text-foreground">{summary.totalKabum}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-foreground/40" />
            <span className="text-muted-foreground">Terabyte</span>
            <span className="font-mono font-semibold text-foreground">{summary.totalTera}</span>
          </div>
          {summary.peakDay && (
            <div className="ml-auto text-xs text-muted-foreground">
              pico <span className="text-foreground tabular-nums">{summary.peakValue}</span> em <span className="text-foreground tabular-nums">{summary.peakDay}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-6 h-64 animate-pulse rounded-lg bg-muted/40" />
        ) : error ? (
          <FetchError what="a coleta diária" onRetry={fetchData} className="mt-6 h-64 sm:h-72" />
        ) : (
          <div className="mt-6 h-64 w-full sm:h-72">
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} debounce={50}>
              <AreaChart data={dadosDiarios} margin={{ top: 8, right: 4, bottom: 4, left: -24 }}>
                <defs>
                  <linearGradient id="kabumGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--brand-amber)" stopOpacity="0.28" />
                    <stop offset="100%" stopColor="var(--brand-amber)" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="teraGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity="0.14" />
                    <stop offset="100%" stopColor="hsl(var(--foreground))" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                <XAxis dataKey="dia" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={1} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={32} />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: "hsl(var(--border))", strokeDasharray: "3 3" }} />
                <Area type="monotone" dataKey="kabum" name="KaBuM!" stroke="var(--brand-amber)" strokeWidth={1.75} fill="url(#kabumGradient)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                <Area type="monotone" dataKey="terabyte" name="Terabyte" stroke="hsl(var(--foreground) / 0.45)" strokeWidth={1.5} fill="url(#teraGradient)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Desconto médio — horizontal bars editorial */}
      <div className="surface-card min-w-0">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Profundidade</div>
            <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">
              Desconto médio por loja
            </h3>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 space-y-4">
            {[...Array(2)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded bg-muted/40" />)}
          </div>
        ) : error ? (
          <FetchError what="o desconto por loja" onRetry={fetchData} className="mt-6" />
        ) : dadosLojas.length === 0 ? (
          <div className="mt-6 text-sm text-muted-foreground">Sem dados ainda.</div>
        ) : (
          <div className="mt-6 space-y-5">
            {dadosLojas.map((l) => (
              <div key={l.loja}>
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-foreground">{l.loja}</span>
                  <div className="flex items-baseline gap-2 tabular-nums">
                    <span className="text-2xl font-semibold tracking-tight text-foreground">{l.avgDesconto}%</span>
                    <span className="text-xs text-muted-foreground">média</span>
                  </div>
                </div>
                <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-border/60">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-out"
                    style={{ width: `${(l.avgDesconto / maxLojaPct) * 100}%`, background: "var(--brand-amber)" }}
                  />
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground tabular-nums">
                  base de <span className="text-foreground">{l.totalProdutos}</span> produtos
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
